import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useGetUserProfileQuery } from '../services/userApi';
import { useGetPublicXPQuery } from '../services/xpApi';
import RoleBadge, { getPrimaryRole } from '../components/RoleBadge';
import styles from '../css/UserProfile.module.css';

const getInitials = (name = '') =>
  name.split(/[_\s]/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');

const Avatar = ({ url, login, size }) => {
  const [imgLoaded, setImgLoaded] = useState(false);

  if (!login) return <div className={styles.avatarSkeleton} />;

  return (
    <div className={styles.avatar}>
      {url ? (
        <>
          {!imgLoaded && <div className={styles.avatarImgSkeleton} />}
          <img
            src={url}
            alt=""
            onLoad={() => setImgLoaded(true)}
            style={{ display: imgLoaded ? 'block' : 'none' }}
          />
        </>
      ) : (
        getInitials(login)
      )}
    </div>
  );
};

const UserProfile = () => {
  const { userId } = useParams();

  const {
    data: profileData,
    isLoading: profileLoading,
    isError: profileError,
    error: profileErr,
  } = useGetUserProfileQuery(userId);

  const {
    data: xpData,
    isLoading: xpLoading,
  } = useGetPublicXPQuery(userId);

  const is404 = profileErr?.status === 404;

  if (is404) {
    return (
      <div className={styles.profilePage}>
        <div className={styles.notFound}>
          <div className={styles.notFoundIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <p className={styles.notFoundText}>Пользователь не найден</p>
          <Link to="/top-users" className={styles.backBtn}>К топу пользователей</Link>
        </div>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className={styles.profilePage}>
        <div className={styles.notFound}>
          <p className={styles.notFoundText}>Не удалось загрузить профиль</p>
        </div>
      </div>
    );
  }

  const profile = profileData?.data ?? null;
  const level = xpData?.data?.level ?? null;
  const isLoading = profileLoading;

  return (
    <div className={styles.profilePage}>
      <Helmet>
        <title>{profile?.login ? `${profile.login} — DoramaHype` : 'Профиль — DoramaHype'}</title>
      </Helmet>

      {/* ── Avatar + Name ── */}
      <div className={styles.hero}>
        {isLoading ? (
          <div className={styles.avatarSkeleton} />
        ) : (
          <Avatar url={profile?.avatar_url} login={profile?.login || '?'} />
        )}
        {isLoading ? (
          <div className={styles.loginSkeleton} />
        ) : (
          <h1 className={styles.login}>
            {profile?.login || 'Пользователь'}
            <RoleBadge role={getPrimaryRole(profile?.roles ?? [])} />
          </h1>
        )}
      </div>

      {/* ── Stats ── */}
      <div className={styles.stats}>
        <div className={styles.statItem}>
          {isLoading || xpLoading ? (
            <span className={styles.statNumSkeleton} />
          ) : (
            <span className={styles.statNum}>{level ?? '—'}</span>
          )}
          <span className={styles.statLabel}>Уровень</span>
        </div>
      </div>

    </div>
  );
};

export default UserProfile;
